import { useState } from 'react';
import { TablePagination } from '../components/TablePagination';
import { SimpleGrid, Card, Text, Title, Group, Button, Table, Loader, Center, Badge, Select } from '@mantine/core';
import { MonthPickerInput, DatePickerInput } from '@mantine/dates';
import { BarChart } from '@mantine/charts';
import { IconDownload, IconChartBar, IconCoin, IconAlertTriangle } from '@tabler/icons-react';
import { useQuery } from '@tanstack/react-query';
import { useDebouncedValue } from '@mantine/hooks';
import { useTranslation } from 'react-i18next';
import api from '../services/api';
import { useRevenue } from '../hooks/useLookups';
import { usePagedQuery } from '../hooks/usePagedQuery';
import { PageHeader } from '../components/PageHeader';
import { SectionCard } from '../components/SectionCard';
import { TableToolbar } from '../components/TableToolbar';
import { FetchOverlay } from '../components/FetchOverlay';
import { formatSom, formatSomShort } from '../lib/format';
import { recentYears } from '../lib/month';
import { exportCsv } from '../lib/csv';

interface ClassCount { classId: string; className: string; count: number }
interface Debtor { studentId: string; fullName: string; className: string; due: number; paid: number; debt: number }
/** /reports/debtors javobidagi qo'shimcha maydonlar (PagedResp ustiga) */
interface DebtorsMeta { month: string; totalDebt: number; filteredDebt: number }

/** Tushum grafigi uchun: joriy yil + oldingi 4 yil (To'lovlar sahifasi ham shu ro'yxatni ishlatadi) */
const YEAR_OPTIONS = recentYears();
const YEAR = YEAR_OPTIONS[0];
const DEBTORS_PER_PAGE = 10;
const ATT_KEYS = ['PRESENT', 'ABSENT', 'LATE', 'EXCUSED'] as const;
const ATT_COLORS: Record<(typeof ATT_KEYS)[number], string> = { PRESENT: 'teal', ABSENT: 'red', LATE: 'orange', EXCUSED: 'blue' };

/** "YYYY-MM" (joriy oy) — qarzdorlar MonthPickerInput default qiymati uchun. */
const thisMonth = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
};
/** Lokal sana → "YYYY-MM-DD". toISOString() EMAS — UTC+5 da kun siljib ketardi. */
const iso = (d: Date | null) =>
  d ? `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}` : undefined;

export default function Reports() {
  const { t } = useTranslation();
  const monthNames = t('monthsShort', { returnObjects: true }) as string[];
  const [year, setYear] = useState(YEAR);
  // Davomat kartalari — sana oralig'i (bo'sh bo'lsa butun tarix, avvalgidek)
  const [attRange, setAttRange] = useState<[Date | null, Date | null]>([null, null]);
  const [debtorSearch, setDebtorSearch] = useState('');
  const [debtorQ] = useDebouncedValue(debtorSearch, 300);
  const [debtorSort, setDebtorSort] = useState('debt:desc');
  const [debtorMonth, setDebtorMonth] = useState<string>(thisMonth());
  const [exporting, setExporting] = useState(false);

  const { data: byClass } = useQuery({ queryKey: ['rep-class'], queryFn: async () => (await api.get<ClassCount[]>('/reports/students-by-class')).data });
  const attParams = { from: iso(attRange[0]), to: iso(attRange[1]) };
  const { data: attendance } = useQuery({
    queryKey: ['rep-att', attParams],
    queryFn: async () => (await api.get<Record<string, number>>('/reports/attendance', { params: attParams })).data,
  });
  const { data: revenue } = useRevenue(year);

  const {
    items: debtors, total: debtorTotal, pages: debtorPages, page: debtorPage, setPage: setDebtorPage,
    limit: debtorLimit, setLimit: setDebtorLimit,
    isLoading: debtorsLoading, fetching: debtorsFetching, data: debtorsRaw,
  } = usePagedQuery<Debtor>(
    'rep-debtors',
    '/reports/debtors',
    { month: debtorMonth, q: debtorQ || undefined, sort: debtorSort },
    DEBTORS_PER_PAGE
  );
  // month / totalDebt / filteredDebt — PagedResp'dan tashqaridagi maydonlar
  const debtorMeta = debtorsRaw as unknown as DebtorsMeta | undefined;

  // Eksport joriy SAHIFANI emas, filtrlangan TO'LIQ ro'yxatni yuklaydi (all=1)
  const exportDebtors = async () => {
    setExporting(true);
    try {
      const { data } = await api.get<{ month: string; items: Debtor[] }>('/reports/debtors', {
        params: { month: debtorMonth, q: debtorQ || undefined, sort: debtorSort, all: 1 },
      });
      exportCsv(
        `qarzdorlar-${data.month}`,
        data.items.map((d) => ({
          [t('att.student')]: d.fullName,
          [t('reports.class')]: d.className,
          [t('reports.plan')]: d.due,
          [t('reports.paidCol')]: d.paid,
          [t('reports.debtCol')]: d.debt,
        }))
      );
    } finally {
      setExporting(false);
    }
  };

  const attTotal = attendance ? Object.values(attendance).reduce((a, b) => a + b, 0) : 0;

  return (
    <>
      <PageHeader title={t('pages.reports.title')} />

      <SimpleGrid cols={{ base: 1, lg: 2 }} spacing="md" mb="md">
        <SectionCard
          title={t('reports.byClass')}
          icon={IconChartBar}
          right={
            <Button
              size="xs"
              variant="light"
              leftSection={<IconDownload size={14} />}
              onClick={() =>
                exportCsv(
                  'sinflar-oquvchilar',
                  (byClass ?? []).map((c) => ({ [t('reports.class')]: c.className, [t('reports.students')]: c.count }))
                )
              }
            >
              Excel
            </Button>
          }
        >
          {byClass?.length ? (
            <BarChart
              h={240}
              data={byClass.map((c) => ({ sinf: c.className, soni: c.count }))}
              dataKey="sinf"
              series={[{ name: 'soni', color: 'brand.6', label: t('charts.count') }]}
            />
          ) : (
            <Center h={200}><Text c="dimmed">{t('reports.noData')}</Text></Center>
          )}
        </SectionCard>

        <SectionCard
          title={t('reports.monthlyRevenue', { year })}
          icon={IconCoin}
          color="teal"
          right={
            <Select
              aria-label={t('reports.year')}
              data={YEAR_OPTIONS}
              value={year}
              onChange={(v) => v && setYear(v)}
              allowDeselect={false}
              size="xs"
              w={100}
            />
          }
        >
          {revenue?.length ? (
            <BarChart
              h={240}
              data={revenue.map((r) => ({ oy: monthNames[Number(r.month.slice(5)) - 1] ?? r.month.slice(5), summa: r.total, kassa: r.cash }))}
              dataKey="oy"
              withLegend
              series={[
                { name: 'summa', color: 'teal.6', label: t('charts.monthIncome') },
                { name: 'kassa', color: 'blue.5', label: t('charts.cashIncome') },
              ]}
              valueFormatter={(v) => formatSom(v)}
              yAxisProps={{ width: 58, tickFormatter: (v: number) => formatSomShort(v) }}
            />
          ) : (
            <Center h={200}><Text c="dimmed">{t('reports.noData')}</Text></Center>
          )}
          {/* Ikki summa nega farq qilishini aytamiz — aks holda "raqamlar mos emas" deb o'ylanardi */}
          {!!revenue?.length && <Text fz="xs" c="dimmed" mt="xs">{t('reports.revenueHint')}</Text>}
        </SectionCard>
      </SimpleGrid>

      {/* Davomat — sana oralig'i (lokal "YYYY-MM-DD"; bo'sh bo'lsa butun tarix) */}
      <Group justify="space-between" align="flex-end" mb="sm" wrap="wrap">
        <Title order={5}>{t('nav.attendance')}</Title>
        <DatePickerInput
          type="range"
          label={t('att.dateRange')}
          value={attRange}
          onChange={setAttRange}
          valueFormat="DD.MM.YYYY"
          clearable
          w={230}
        />
      </Group>

      <SimpleGrid cols={{ base: 2, sm: 4 }} spacing="md" mb="md">
        {ATT_KEYS.map((k) => {
          const val = attendance?.[k] ?? 0;
          const pct = attTotal ? Math.round((val / attTotal) * 100) : 0;
          return (
            <Card key={k}>
              <Text size="xl" fw={700} c={`${ATT_COLORS[k]}.7`}>{val}</Text>
              <Text size="sm" c="dimmed">{t(`att.${k}`)} · {pct}%</Text>
            </Card>
          );
        })}
      </SimpleGrid>

      <SectionCard
        title={`${t('reports.debtors')} (${debtorMeta?.month ?? ''})`}
        icon={IconAlertTriangle}
        color="red"
        subtitle={
          `${t('reports.totalDebt')}: ${formatSom(debtorMeta?.totalDebt)}` +
          (debtorQ
            ? ` · ${t('reports.found')}: ${t('reports.count', { count: debtorTotal })} (${formatSom(debtorMeta?.filteredDebt)})`
            : ` · ${t('reports.count', { count: debtorTotal })}`)
        }
        right={
          <Button
            size="xs"
            variant="light"
            leftSection={<IconDownload size={14} />}
            loading={exporting}
            disabled={!debtorTotal}
            onClick={exportDebtors}
          >
            Excel
          </Button>
        }
        p="md"
        pos="relative"
      >
        <FetchOverlay visible={debtorsFetching} />

        <TableToolbar
          search={debtorSearch}
          onSearch={(v) => { setDebtorSearch(v); setDebtorPage(1); }}
          searchPlaceholder={t('reports.searchDebtor')}
          sort={debtorSort}
          onSort={(v) => { setDebtorSort(v); setDebtorPage(1); }}
          sortOptions={[
            { value: 'debt:desc', label: t('reports.sortDebtDesc') },
            { value: 'debt:asc', label: t('reports.sortDebtAsc') },
            { value: 'fullName:asc', label: t('reports.sortNameAsc') },
            { value: 'fullName:desc', label: t('reports.sortNameDesc') },
            { value: 'className:asc', label: t('reports.sortClassAsc') },
            { value: 'due:desc', label: t('reports.sortDueDesc') },
            { value: 'paid:desc', label: t('reports.sortPaidDesc') },
          ]}
        >
          <MonthPickerInput
            label={t('fields.month')}
            value={debtorMonth ? new Date(`${debtorMonth}-01`) : null}
            onChange={(d) => {
              setDebtorMonth(d ? `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}` : thisMonth());
              setDebtorPage(1);
            }}
            valueFormat="YYYY MMMM"
            w={170}
          />
        </TableToolbar>

        {debtorsLoading ? (
          <Center h={120}><Loader /></Center>
        ) : !debtors.length ? (
          <Center h={100}><Text c="dimmed">{debtorQ ? t('reports.noMatch') : t('reports.noDebtors')}</Text></Center>
        ) : (
          <>
            <Table.ScrollContainer minWidth={560}>
              <Table verticalSpacing="sm">
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>{t('att.student')}</Table.Th>
                    <Table.Th>{t('reports.class')}</Table.Th>
                    <Table.Th>{t('reports.plan')}</Table.Th>
                    <Table.Th>{t('reports.paidCol')}</Table.Th>
                    <Table.Th>{t('reports.debtCol')}</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {debtors.map((d) => (
                    <Table.Tr key={d.studentId}>
                      <Table.Td fw={500}>{d.fullName}</Table.Td>
                      <Table.Td>{d.className}</Table.Td>
                      <Table.Td>{formatSom(d.due)}</Table.Td>
                      <Table.Td>{formatSom(d.paid)}</Table.Td>
                      <Table.Td><Badge color="red" variant="light">{formatSom(d.debt)}</Badge></Table.Td>
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>
            </Table.ScrollContainer>

            <TablePagination
              page={debtorPage}
              pages={debtorPages}
              total={debtorTotal}
              limit={debtorLimit}
              onPage={setDebtorPage}
              onLimit={setDebtorLimit}
            />
          </>
        )}
      </SectionCard>
    </>
  );
}
